import React, { useEffect, useState } from "react";
import { Link, useParams} from "react-router-dom";
import { useOrderDetailsQuery, useUpdateOrderMutation } from "../../redux/api/orderApi";


import toast from "react-hot-toast";
import { Helmet } from "react-helmet-async";

const ProcessOrder = () => {
    const [status, setStatus] = useState("");

    const params = useParams();
    const { data } = useOrderDetailsQuery(params?.id);
    const order = data?.order || {};
  
    const [updateOrder, { error, isSuccess }] = useUpdateOrderMutation();
  
    const {
      shippingInfo,
      orderItems,
      paymentInfo,
      totalAmount,
      orderStatus,
    } = order;
  
    const isPaid = paymentInfo?.status === "paid" ? true : false;
  
    useEffect(() => {
      if (orderStatus) {
        setStatus(orderStatus);
      }
    }, [orderStatus]);
  
    useEffect(() => {
      if (error) {
        toast.error(error?.data?.message);
      }
  
      if (isSuccess) {
        toast.success("Order updated");
      }
    }, [error, isSuccess]);
  
    const updateOrderHandler = (id) => {
      const data = { status };
      updateOrder({ id, body: data });
    };
  

  const steps = ["Placed", "Processing", "Shipped", "Delivered"];
  const currentStep = steps.indexOf(orderStatus);

  

  return (
    <>
       <Helmet>
        <title>Process Order</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="max-w-6xl md:mt-44 mt-16 mx-auto px-4 lg:px-8 py-10 font-sourcesans grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Order Progress */}
          <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-sm">
            <h2 className="text-2xl font-playfair text-[#BC3F4A] mb-4">Process Order</h2>
            <div className="flex justify-between items-center text-sm font-medium text-stone-600">
              {steps.map((step, idx) => (
                <div
                  key={step}
                  className={`flex-1 flex flex-col items-center ${
                    idx <= currentStep ? "text-[#BC3F4A]" : "text-stone-400"
                  }`}
                >
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full border-2 ${
                      idx <= currentStep
                        ? "border-[#BC3F4A] bg-[#BC3F4A] text-white"
                        : "border-stone-300 bg-white"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <span className="mt-2">{step}</span>
                </div>
              ))}
            </div>

            {/* Update Status */}
            <div className="mt-6 flex items-center gap-4">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border rounded-md px-3 py-2"
                disabled={status === "Delivered"}
              >
                {steps.map((step) => (
                  <option key={step} value={step}>
                    {step}
                  </option>
                ))}
              </select>
              <button
                  onClick={() => updateOrderHandler(order?._id)}
                
                className="px-4 py-2 bg-[#BC3F4A]
                 text-white rounded-md hover:bg-[#a3323b]
                  transition disabled:opacity-50"
              >
               Update Status
              </button>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-sm">
            <h3 className="text-xl font-playfair text-[#BC3F4A] mb-4">Shipping Information</h3>
            <p><span className="font-semibold">Name:</span> {order?.user?.name || "N/A"}</p>
            <p><span className="font-semibold">Email:</span> {order?.user?.email || "N/A"}</p>
            <p><span className="font-semibold">Phone:</span> {shippingInfo?.phoneNo || "N/A"}</p>
            <p>
              <span className="font-semibold">Address:</span>{" "}
              {shippingInfo?.address || "N/A"}, {shippingInfo?.city || "N/A"}, {shippingInfo?.region || "N/A"}, 
              {shippingInfo?.zipCode || "N/A"}, {shippingInfo?.country || "N/A"}
            </p>
          </div>

          {/* Payment Info */}
          <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-sm">
            <h3 className="text-xl font-playfair text-[#BC3F4A] mb-4">Payment</h3>
            <p
              className={`inline-block px-3 py-1 rounded-md text-sm font-medium ${
                isPaid
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {paymentInfo?.status?.toUpperCase() || "UNPAID"}
            </p>
            <p className="mt-2 text-stone-700">
              <span className="font-semibold">Stripe ID:</span> {paymentInfo?.id || "—"}
            </p>
            <p className="mt-2 text-stone-700">
              <span className="font-semibold">Amount:</span> ${totalAmount?.toFixed(2)}
            </p>
          </div>

          {/* Ordered Items */}
          <div className="bg-white p-6 rounded-lg border border-stone-200 shadow-sm">
            <h3 className="text-xl font-playfair text-[#BC3F4A] mb-4">Ordered Items</h3>
            <ul className="divide-y divide-stone-200">
              {orderItems?.length > 0 ? (
                orderItems?.map((item) => (
                  <li key={item._id} className="flex justify-between items-center py-4">
                    <div className="flex items-center gap-4">
                      <img src={item?.image} alt={item?.name} className="w-16 h-16 object-contain rounded border border-stone-200" />
                      <span className="text-stone-800 font-medium"> <Link to={`/products/${item?.product}`}>{item?.name}</Link></span>
                    </div>
                    <p className="text-stone-700">
                      {item?.quantity || 0} × ${item?.price || 0} ={" "}
                      <span className="font-semibold">${((item?.quantity || 0) * (item?.price || 0)).toFixed(2)}</span>
                    </p>
                  </li>
                ))
              ) : (
                <p className="text-stone-700">No items found</p>
              )}
            </ul>
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 bg-[#fdfcf8] p-6 rounded-lg border border-stone-200 shadow-md">
            <h3 className="text-xl font-playfair text-[#BC3F4A] mb-4">Order Summary</h3>
            <p className="text-stone-700 mb-2"><span className="font-semibold">Order ID:</span> {order?._id?.substring(0, 10) || ""}...</p>
            <p className="text-stone-700 mb-2"><span className="font-semibold">Status:</span> {status || "N/A"}</p>
            <p className="text-stone-700 mb-4"><span className="font-semibold">Total:</span> ${totalAmount?.toFixed(2) || "0.00"}</p>
            <div className="mt-10">   <Link
                to={`/receipt/order/${order?._id}`}
                className="w-full px-4 py-2 text-center rounded-md bg-stone-700 text-white hover:bg-stone-800 transition"
              >
                <i className="fa fa-print mr-1"></i> Download Receipt
              </Link></div>
          </div>
         
       
        </aside>
       
      </section>
    </>
  );
};

export default ProcessOrder;
